import { Component, createContext, ReactNode } from 'react';

import { BraxTabProps } from '../BraxTab';

export type TabsContextValue<T extends string = string> = {
    animating?: T | null,
    active?: T,
    unmount: boolean,
    prerender: boolean,
    hidden: boolean,
    className: boolean,
    add: (tab: BraxTabProps<T>) => void,
    remove: (id: T) => void,
    setTitle: (id: T, title: ReactNode) => void,
    setDisabled: (id: T, disabled: boolean) => void,
    tabs: BraxTabProps<T>[]
};

const TabsContext = createContext<TabsContextValue>({} as any);

TabsContext.displayName = 'TabsContext';

export type TabsProviderProps<T extends string = string> = Pick<TabsContextValue<T>, 'active' | 'animating' | 'prerender' | 'unmount' | 'hidden' | 'className'> & {
    children: ReactNode
};

type TabsProviderState<T extends string = string> = Omit<TabsContextValue<T>, 'active' | 'prerender' | 'unmount' | 'hidden' | 'className'>;

export class TabsProvider<T extends string = string> extends Component<TabsProviderProps<T>, TabsProviderState<T>> {

    state: TabsProviderState<T> = {
        add: this.add.bind(this),
        remove: this.remove.bind(this),
        setTitle: this.setTitle.bind(this),
        setDisabled: this.setDisabled.bind(this),
        tabs: []
    };

    add(tab: BraxTabProps<T>) {
        this.setState(prevState => ({
            tabs: [...prevState.tabs, tab]
        }));
    }

    remove(id: T) {
        this.setState(prevState => ({
            tabs: prevState.tabs.filter(tab => tab.id !== id)
        }));
    }

    setTitle(id: T, title: ReactNode) {
        this.setState(prevState => ({
            tabs: prevState.tabs.map(tab => tab.id === id ? ({ ...tab, title }) : tab)
        }));
    }

    setDisabled(id: T, disabled: boolean) {
        this.setState(prevState => ({
            tabs: prevState.tabs.map(tab => tab.id === id ? ({ ...tab, disabled }) : tab)
        }));
    }

    render() {
        const { active, children, ...props } = this.props;

        return (
            <TabsContext.Provider value={{ active: this.props.active || this.state.tabs[0]?.id, ...props, ...this.state }}>
                { this.props.children }
            </TabsContext.Provider>
        );
    }

}

export class TabsConsumer<T extends string = string> extends Component<{ children: (value: TabsContextValue<T>) => ReactNode }> {

    render() {
        return (
            <TabsContext.Consumer>
                { this.props.children }
            </TabsContext.Consumer>
        );
    }

}

export default TabsContext;
